<script setup lang="ts">
/* =============================================================
   BERTH — CREATE / EDIT
   =============================================================
   One page, two jobs, told apart by whether the route carries an
   id. Sharing them is worth it here because every rule below —
   validation, the unsaved-changes guard, where you land on save
   — is identical, and two copies would drift.

   A page rather than a dialog: the berth has six more fields
   waiting in docs/data-model.md (address, width, draft, pricing,
   photo, status), and a modal that needs its own scrollbar has
   stopped being a modal.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUnsavedGuard } from '@/lib/useUnsavedGuard'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import SelectField from '@/components/ui/SelectField.vue'
import CountrySelect from '@/components/ui/CountrySelect.vue'
import AmenityPicker from '@/components/ui/AmenityPicker.vue'
import Alert from '@/components/ui/Alert.vue'
import { routes, to } from '@/config/routes'
import { createBerth, getBerth, updateBerth } from '@/lib/berths'
import { listCompanies } from '@/lib/companies'
import { DataError } from '@/lib/errors'
import { BOOKING_MODES } from '@/lib/bookingLabels'
import type { AmenityCode, BerthInput, BookingMode, Company } from '@/lib/types'

const route = useRoute()
const router = useRouter()

const editingId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => editingId.value !== null)

interface FormState {
  name: string
  city: string
  country_code: string
  latitude: string
  longitude: string
  /** The <select> value, so '' means nothing chosen. Parsed on submit. */
  company_id: string
  amenities: AmenityCode[]
  /** Blank means "not stated", which is a real answer — see validate(). */
  max_length_m: string
  max_vessels: string
  booking_mode: BookingMode
  /** Only shown, and only sent, under manual. */
  response_hours: string
}

const blank: FormState = {
  name: '',
  city: '',
  country_code: '',
  latitude: '',
  longitude: '',
  company_id: '',
  amenities: [],
  max_length_m: '',
  max_vessels: '',
  booking_mode: 'instant',
  response_hours: '',
}

const form = ref<FormState>({ ...blank })
/** What was loaded, to tell a real edit from a visit. */
const pristine = ref<FormState>({ ...blank })
const heading = ref('New berth')

const loading = ref(false)
const saving = ref(false)
const notFound = ref(false)
const formError = ref('')
const errors = ref<Record<string, string>>({})

/*
 * The owner list, fetched per visit rather than cached in a module the way
 * countries are. Companies are created and renamed in this very panel, so a
 * shared copy would be stale exactly when it matters — right after someone
 * adds the company they came here to attach a berth to.
 */
const companies = ref<Company[]>([])
const companiesFailed = ref(false)

async function loadCompanies(): Promise<void> {
  try {
    companies.value = await listCompanies()
    companiesFailed.value = false
  } catch {
    companiesFailed.value = true
  }
}

/*
 * A berth cannot exist without an owner — `berths.company_id` is NOT NULL —
 * so with no company on the platform there is nothing valid to submit. Saying
 * so beats an empty dropdown and a foreign key error.
 */
const noCompanies = computed(() => !companiesFailed.value && companies.value.length === 0)

const dirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(pristine.value))

onMounted(async () => {
  if (!isEdit.value) {
    await loadCompanies()
    return
  }
  loading.value = true
  try {
    // Both at once: neither needs the other, and the form cannot be used
    // until both have arrived.
    const [berth] = await Promise.all([getBerth(editingId.value as number), loadCompanies()])
    if (!berth) {
      notFound.value = true
      return
    }
    const state: FormState = {
      name: berth.name,
      city: berth.city,
      country_code: berth.country_code,
      latitude: String(berth.latitude),
      longitude: String(berth.longitude),
      company_id: String(berth.company_id),
      amenities: [...berth.amenities],
      max_length_m: berth.max_length_m === null ? '' : String(berth.max_length_m),
      max_vessels: berth.max_vessels === null ? '' : String(berth.max_vessels),
      booking_mode: berth.booking_mode,
      response_hours: berth.response_hours === null ? '' : String(berth.response_hours),
    }
    form.value = { ...state }
    pristine.value = { ...state }
    heading.value = berth.name
  } catch (e) {
    formError.value = e instanceof DataError ? e.message : 'Could not load the berth.'
  } finally {
    loading.value = false
  }
})

/**
 * Mirrors the CHECK constraints in
 * supabase/migrations/20260919063544_create_berths.sql. The database is still
 * the authority — this only puts a typo in the field it belongs to rather than
 * returning one message about the whole row.
 */
function validate(): BerthInput | null {
  const next: Record<string, string> = {}

  const name = form.value.name.trim()
  if (!name) next.name = 'Enter a name.'

  const city = form.value.city.trim()
  if (!city) next.city = 'Enter a city.'

  const country_code = form.value.country_code
  if (!country_code) next.country_code = 'Choose a country.'

  const lat = Number(form.value.latitude)
  if (form.value.latitude.trim() === '' || Number.isNaN(lat)) next.latitude = 'Enter a number.'
  else if (lat < -90 || lat > 90) next.latitude = 'Must be between -90 and 90.'

  const lng = Number(form.value.longitude)
  if (form.value.longitude.trim() === '' || Number.isNaN(lng)) next.longitude = 'Enter a number.'
  else if (lng < -180 || lng > 180) next.longitude = 'Must be between -180 and 180.'

  const company_id = Number(form.value.company_id)
  if (!form.value.company_id || Number.isNaN(company_id)) next.company_id = 'Choose an owner.'

  /* Both size limits are optional, so blank is valid and means "the source
     does not say". Only a value that is present and wrong is an error. The
     bounds mirror the CHECK constraints in
     supabase/migrations/20260919085213_add_berth_capacity.sql — the 5 m floor
     on length exists to catch ship counts written into the length column,
     which the master ports spreadsheet is full of. */
  const rawLen = form.value.max_length_m.trim()
  const len = rawLen === '' ? null : Number(rawLen)
  if (len !== null && (Number.isNaN(len) || len <= 5))
    next.max_length_m = 'Enter a length in metres over 5, or leave it blank.'

  const rawVessels = form.value.max_vessels.trim()
  const vessels = rawVessels === '' ? null : Number(rawVessels)
  if (vessels !== null && (!Number.isInteger(vessels) || vessels < 1))
    next.max_vessels = 'Enter a whole number of vessels, or leave it blank.'

  /* Manual mode needs a response time and instant must not have one --
     berths_response_hours_iff_manual. Under instant the field is hidden and
     whatever it held is dropped by clean() in berths.ts. */
  const booking_mode = form.value.booking_mode
  const rawResponse = form.value.response_hours.trim()
  const response = rawResponse === '' ? null : Number(rawResponse)
  if (booking_mode === 'manual' && (response === null || !Number.isInteger(response) || response < 1))
    next.response_hours = 'Enter how many hours the owner usually takes to answer.'

  // No rule on amenities: an empty set is a valid answer, and the enum makes
  // an invalid one unrepresentable.

  // Every check above, then publish and decide — in that order. Writing to
  // `next` after this point would neither show (the ref holds the raw object,
  // so a later mutation misses the proxy and nothing re-renders) nor stop the
  // submit, which is how the two optional limits were once bypassed entirely.
  errors.value = next
  if (Object.keys(next).length > 0) return null

  return {
    name,
    city,
    country_code,
    latitude: lat,
    longitude: lng,
    company_id,
    amenities: form.value.amenities,
    max_length_m: len,
    max_vessels: vessels,
    booking_mode,
    response_hours: booking_mode === 'manual' ? response : null,
  }
}

async function onSubmit(): Promise<void> {
  if (saving.value) return
  const input = validate()
  if (!input) return

  saving.value = true
  formError.value = ''
  try {
    const saved = isEdit.value
      ? await updateBerth(editingId.value as number, input)
      : await createBerth(input)
    // Clear the guard before navigating, or leaving prompts about changes
    // that were just written.
    pristine.value = { ...form.value }
    await router.push(to.berth(saved.id))
  } catch (e) {
    formError.value = e instanceof DataError ? e.message : 'Could not save the berth.'
  } finally {
    saving.value = false
  }
}

const cancelTo = computed(() =>
  isEdit.value ? to.berth(editingId.value as number) : routes.berths,
)

// Asks before leaving with unsaved edits -- see lib/useUnsavedGuard.ts.
useUnsavedGuard(dirty, saving)
</script>

<template>
  <PageHeader
    :title="isEdit ? `Edit ${heading}` : 'New berth'"
    description="Coordinates are decimal degrees. The map position is derived from them by the database."
  />

  <p v-if="loading" class="page-status">Loading…</p>

  <Alert v-else-if="notFound" tone="danger">
    That berth does not exist. <RouterLink :to="routes.berths">Back to all berths</RouterLink>.
  </Alert>

  <Panel v-else>
    <form class="form-layout" novalidate @submit.prevent="onSubmit">
      <Alert v-if="formError" tone="danger">{{ formError }}</Alert>

      <Alert v-if="noCompanies" tone="warning">
        There are no companies yet, and every berth belongs to one.
        <RouterLink :to="routes.companyNew">Add a company</RouterLink> first.
      </Alert>

      <div class="form-grid">
        <TextField
          v-model="form.name"
          label="Name"
          :error="errors.name"
          placeholder="Rijnhaven Kade 3"
          autocomplete="off"
          class="form-wide"
          required
        />
        <SelectField
          v-model="form.company_id"
          label="Owner"
          placeholder="Select a company"
          :error="
            errors.company_id ??
            (companiesFailed ? 'The company list could not be loaded. Reload the page.' : undefined)
          "
          :disabled="companiesFailed || companies.length === 0"
          class="form-wide"
        >
          <option v-for="c in companies" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
        </SelectField>
        <TextField
          v-model="form.city"
          label="City"
          :error="errors.city"
          placeholder="Rotterdam"
          autocomplete="off"
          required
        />
        <CountrySelect v-model="form.country_code" :error="errors.country_code" />
        <TextField
          v-model="form.latitude"
          label="Latitude"
          :error="errors.latitude"
          inputmode="decimal"
          placeholder="51.9043"
        />
        <TextField
          v-model="form.longitude"
          label="Longitude"
          :error="errors.longitude"
          inputmode="decimal"
          placeholder="4.4856"
        />
        <TextField
          v-model="form.max_length_m"
          label="Max vessel length"
          :error="errors.max_length_m"
          hint="Optional, metres."
          inputmode="decimal"
          placeholder="135"
          autocomplete="off"
        />
        <TextField
          v-model="form.max_vessels"
          label="Max vessels at once"
          :error="errors.max_vessels"
          hint="Optional."
          inputmode="numeric"
          placeholder="2"
          autocomplete="off"
        />
        <SelectField
          v-model="form.booking_mode"
          label="Booking mode"
          :hint="BOOKING_MODES.find((m) => m.code === form.booking_mode)?.hint"
        >
          <option v-for="m in BOOKING_MODES" :key="m.code" :value="m.code">{{ m.label }}</option>
        </SelectField>
        <TextField
          v-if="form.booking_mode === 'manual'"
          v-model="form.response_hours"
          label="Usually answers within"
          :error="errors.response_hours"
          hint="Hours. Shown to the requester; not enforced."
          inputmode="numeric"
          placeholder="24"
          autocomplete="off"
        />
        <AmenityPicker
          v-model="form.amenities"
          hint="What this berth offers. Leaving all of them unticked is a valid answer."
          class="form-wide"
        />
      </div>

      <footer class="form-actions">
        <Button variant="ghost" :to="cancelTo" :disabled="saving">Cancel</Button>
        <Button type="submit" :loading="saving" :disabled="noCompanies">
          {{ isEdit ? 'Save changes' : 'Create berth' }}
        </Button>
      </footer>
    </form>
  </Panel>
</template>
